/**
 * Справочник («шпаргалка»): темы с базовыми словами вне программы —
 * глаголы, числа, дни, цвета. Темы и карточки отдаёт сервер уже на языке
 * интерфейса ученика; прогресс тем хранит клиент.
 */

import { z } from 'zod';

import { LanguageCodeSchema, type LanguageCode } from './language';

/** Пример к правилу: фраза изучаемого языка и перевод. */
export const GrammarExampleSchema = z.object({ target: z.string().min(1), translation: z.string().min(1) });
export type GrammarExample = z.infer<typeof GrammarExampleSchema>;

/** «Собери формулу»: блоки формулы по порядку и лишние блоки-ловушки. */
export const GrammarFormulaGameSchema = z.object({
  /** Название формулы: «Present Simple: отрицание». */
  name: z.string().min(1),
  blocks: z.array(z.string().min(1)).min(2).max(6),
  decoys: z.array(z.string().min(1)).max(3),
});
export type GrammarFormulaGame = z.infer<typeof GrammarFormulaGameSchema>;

/** «Найди ошибку»: предложение с типичной ошибкой, слово, которое надо исправить, и как правильно. */
export const GrammarSpotGameSchema = z.object({ wrong: z.string().min(1), word: z.string().min(1), right: z.string().min(1) });
export type GrammarSpotGame = z.infer<typeof GrammarSpotGameSchema>;

/** «Выбери форму»: предложение с пропуском `___`, варианты и верный. */
export const GrammarGapGameSchema = z.object({ sentence: z.string().min(1), options: z.array(z.string().min(1)).min(3).max(4), answer: z.string().min(1) });
export type GrammarGapGame = z.infer<typeof GrammarGapGameSchema>;

/** Игры правила: формулы, ошибки, пропуски. */
export const GrammarGamesSchema = z.object({
  formulas: z.array(GrammarFormulaGameSchema).min(1).max(3),
  spot: z.array(GrammarSpotGameSchema).min(1).max(4),
  gaps: z.array(GrammarGapGameSchema).min(1).max(5),
});
export type GrammarGames = z.infer<typeof GrammarGamesSchema>;

/**
 * Правило в «Курсе грамматики»: формула, лайфхак и аналогия простыми
 * словами, примеры с озвучкой и типичная ошибка русскоязычных.
 */
export const GrammarDetailsSchema = z.object({
  /** Формула: «he / she / it + глагол-s». */
  formula: z.string().min(1),
  /** Лайфхак: как запомнить за минуту. */
  hack: z.string().min(1),
  /** Аналогия из жизни. */
  analogy: z.string().min(1),
  examples: z.array(GrammarExampleSchema).min(2).max(4),
  /** Типичная ошибка: как не надо и как надо. */
  mistake: z.object({ wrong: z.string().min(1), right: z.string().min(1) }),
  /** Игры правила; у старых записей без игр правило просто не играется. */
  games: GrammarGamesSchema.optional(),
});
export type GrammarDetails = z.infer<typeof GrammarDetailsSchema>;

export const REFERENCE_KINDS = ['words', 'phonetics', 'grammar'] as const;
export type ReferenceKind = (typeof REFERENCE_KINDS)[number];

export const ReferenceCardViewSchema = z.object({
  id: z.string(),
  /** Слово изучаемого языка. */
  word: z.string(),
  /** Перевод на язык интерфейса. */
  translation: z.string(),
  /** Заглушка картинки, пока ассоциативная не нарисована. */
  emoji: z.string(),
  /** Что произносит голос, если не само слово (у звука — слова-примеры); `null` — само слово. */
  speech: z.string().nullable(),
  /** Слова-примеры звука: «cat, bat, map»; `null` — у обычного слова. */
  examples: z.string().nullable(),
  /** Правило грамматики: у карточек темы `grammar`; иначе `null`. */
  grammar: GrammarDetailsSchema.nullable(),
});
export type ReferenceCardView = z.infer<typeof ReferenceCardViewSchema>;

export const ReferenceTopicViewSchema = z.object({
  id: z.string(),
  language: LanguageCodeSchema,
  title: z.string(),
  icon: z.string(),
  description: z.string(),
  /** words — слова; phonetics — звуки и буквы: символ, примеры, озвучка; grammar — правила. */
  kind: z.enum(REFERENCE_KINDS),
  cardCount: z.number().int().nonnegative(),
});
export type ReferenceTopicView = z.infer<typeof ReferenceTopicViewSchema>;

export const ReferenceTopicDetailSchema = ReferenceTopicViewSchema.extend({
  cards: z.array(ReferenceCardViewSchema),
});
export type ReferenceTopicDetail = z.infer<typeof ReferenceTopicDetailSchema>;

/** Доля верных ответов викторины, с которой тема считается освоенной. */
export const REFERENCE_MASTERY_SCORE = 0.8;

/** Прогресс ученика по теме справочника. */
export const ReferenceTopicProgressSchema = z.object({
  /** Карточки, смахнутые вправо («знаю»). */
  known: z.array(z.string()),
  /** Лучшая доля верных ответов викторины, 0…1. */
  quizBest: z.number().min(0).max(1),
  /** Лучшая доля верных в аудировании и диктанте, 0…1. */
  listenBest: z.number().min(0).max(1),
  /** Раундов «Найди пару» без единой ошибки. */
  perfectMatches: z.number().int().nonnegative(),
  /** Карточек в теме: по нему считается освоение. */
  cardCount: z.number().int().nonnegative(),
});
export type ReferenceTopicProgress = z.infer<typeof ReferenceTopicProgressSchema>;

/** Освоена: все карточки «знаю» и викторина не ниже порога. */
export function isTopicMastered(progress: Pick<ReferenceTopicProgress, 'known' | 'quizBest' | 'cardCount'>): boolean {
  return progress.cardCount > 0 && progress.known.length >= progress.cardCount && progress.quizBest >= REFERENCE_MASTERY_SCORE;
}

/**
 * «Песочница новичка»: базовые темы справочника языка. Ученик на A1 без
 * единого урока сначала осваивает `SANDBOX_REQUIRED` из них — до этого
 * сервер уроки программы не отдаёт (`SANDBOX_LOCKED`).
 */
export const SANDBOX_CORE: Readonly<Partial<Record<LanguageCode, readonly ('verbs' | 'numbers' | 'colors')[]>>> = {
  en: ['verbs', 'numbers', 'colors'],
  de: ['verbs', 'numbers', 'colors'],
  es: ['verbs', 'numbers', 'colors'],
};
export const SANDBOX_REQUIRED = 2;

/** id темы песочницы у языка: `en` + `verbs` → `en-verbs`. */
export function sandboxTopicId(language: LanguageCode, key: string): string {
  return `${language}-${key}`;
}

export interface ReferenceProgressView {
  readonly topics: Readonly<Record<string, ReferenceTopicProgress>>;
  /** Песочница пройдена (освоены основы или ученик её пропустил). */
  readonly sandboxPassed: boolean;
}

/** Событие прогресса: сервер применяет его сам — два устройства не затирают друг друга. */
export const ReferenceProgressEventSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('mark'), cardId: z.string().max(80), known: z.boolean() }),
  z.object({ kind: z.literal('quiz'), score: z.number().min(0).max(1) }),
  z.object({ kind: z.literal('listen'), score: z.number().min(0).max(1) }),
  z.object({ kind: z.literal('match'), perfect: z.boolean() }),
  z.object({ kind: z.literal('reset') }),
]);
export type ReferenceProgressEvent = z.infer<typeof ReferenceProgressEventSchema>;

/** Прогресс с прежнего устройства (localStorage) — сливается с серверным один раз. */
export const ReferenceProgressImportSchema = z.object({
  topics: z.record(
    z.string().max(60),
    z.object({
      known: z.array(z.string().max(80)).max(500),
      quizBest: z.number().min(0).max(1),
      listenBest: z.number().min(0).max(1).optional(),
      perfectMatches: z.number().int().nonnegative().max(10_000),
    }),
  ),
  sandboxPassed: z.boolean(),
});
export type ReferenceProgressImport = z.infer<typeof ReferenceProgressImportSchema>;
